import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, IconButton, Paper, Skeleton, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useCommunityDashboard } from "../../hooks/useCommunity";
import { CommunityCountry } from "../../models/CommunityDashboard";
import { zoullooColors } from "../../theme";
import {
	ALL,
	ALL_VALUE,
	CommunityFilterSelection,
	countLocated,
	countMulti,
	countSingle,
	emptySelection,
	filterTalents,
	NOT_LOCATED,
	NOT_LOCATED_VALUE,
	NOT_SPECIFIED,
	sanitizeSelection,
	toFilterIndexes,
	toSortedRows
} from "../../utils/communityAggregation";
import CommunityFilterBar from "../../components/community/CommunityFilterBar";
import DonutCard from "../../components/community/DonutCard";
import HBarCard from "../../components/community/HBarCard";
import KpiRow, { KpiItem } from "../../components/community/KpiRow";
import {
	ChartRow,
	CountUnit,
	formatCount,
	formatPct,
	SelectOption
} from "../../components/community/types";

const categoryColors = [zoullooColors.secondary.main, zoullooColors.primary.main];
const seniorityColors = [
	zoullooColors.secondary.light,
	zoullooColors.secondary.main,
	zoullooColors.secondary.dark
];
const mutedColor = zoullooColors.grey[400];
const fallbackColor = zoullooColors.grey[500];

const categoryColor = (categoryIndex: number): string =>
	categoryColors[categoryIndex] || fallbackColor;

const createRegionDisplayNames = (locale: string): Intl.DisplayNames | null => {
	if (typeof Intl.DisplayNames !== "function") return null;
	try {
		return new Intl.DisplayNames([locale], { type: "region" });
	} catch {
		return null;
	}
};

const countryLabel = (
	country: CommunityCountry | undefined,
	displayNames: Intl.DisplayNames | null,
	fallback: string
): string => {
	if (!country) return fallback;
	if (!displayNames) return country.name;
	try {
		const localised = displayNames.of(country.code);
		return localised && localised !== country.code ? localised : country.name;
	} catch {
		return country.name;
	}
};

const CommunityDashboardSkeleton: React.FC = () => (
	<Box px={4} py={3} data-testid="community-dashboard-skeleton">
		<Skeleton variant="text" width={300} height={40} sx={{ mb: 3 }} />
		<Skeleton variant="rounded" height={56} sx={{ borderRadius: 3, mb: 3 }} />
		<Grid container spacing={3} sx={{ mb: 4 }}>
			{[1, 2, 3, 4].map(i => (
				<Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
					<Skeleton variant="rounded" height={140} sx={{ borderRadius: 3 }} />
				</Grid>
			))}
		</Grid>
		<Grid container spacing={3}>
			{[1, 2, 3, 4, 5, 6, 7].map(i => (
				<Grid size={{ xs: 12, md: 6 }} key={i}>
					<Skeleton variant="rounded" height={280} sx={{ borderRadius: 3 }} />
				</Grid>
			))}
		</Grid>
	</Box>
);

const CommunityDashboard: React.FC = () => {
	const { t, i18n } = useTranslation();
	const { data, isLoading, isError, refetch, isFetching } = useCommunityDashboard();
	const [filters, setFilters] = useState<CommunityFilterSelection>(emptySelection);
	const [unit, setUnit] = useState<CountUnit>("values");

	const locale = i18n.language;
	const dims = data?.dimensions;

	const countryCodesByRegion = useMemo(() => {
		const byRegion = new Map<number, Set<string>>();
		if (!data) return byRegion;
		data.talents.forEach(talent => {
			if (talent.regionIndex === null || talent.countryIndex === null) return;
			const code = data.dimensions.countries[talent.countryIndex]?.code;
			if (!code) return;
			const codes = byRegion.get(talent.regionIndex) ?? new Set<string>();
			codes.add(code);
			byRegion.set(talent.regionIndex, codes);
		});
		return byRegion;
	}, [data]);

	const withCountryInRegion = useCallback(
		(selection: CommunityFilterSelection): CommunityFilterSelection => {
			if (
				!dims ||
				selection.region === ALL_VALUE ||
				selection.country === ALL_VALUE ||
				selection.country === NOT_LOCATED_VALUE
			) {
				return selection;
			}
			const regionIndex = dims.regions.indexOf(selection.region);
			if (countryCodesByRegion.get(regionIndex)?.has(selection.country)) return selection;
			return { ...selection, country: ALL_VALUE };
		},
		[dims, countryCodesByRegion]
	);

	useEffect(() => {
		if (!dims) return;
		setFilters(current => withCountryInRegion(sanitizeSelection(current, dims)));
	}, [dims, withCountryInRegion]);

	const activeFilters = useMemo(
		() => (dims ? withCountryInRegion(sanitizeSelection(filters, dims)) : filters),
		[dims, filters, withCountryInRegion]
	);

	const filterIndexes = useMemo(
		() => (dims ? toFilterIndexes(activeFilters, dims) : null),
		[dims, activeFilters]
	);

	const selection = useMemo(
		() => (data && filterIndexes ? filterTalents(data.talents, filterIndexes) : []),
		[data, filterIndexes]
	);

	const regionDisplayNames = useMemo(() => createRegionDisplayNames(locale), [locale]);

	const translateCategory = (code: string) => t(`ExpertiseCategories.${code}`, code);
	const translateExpertise = (code: string) => t(`Expertises.${code}`, code);
	const translateSeniority = (code: string) => t(`Experiences.${code}`, code);
	const translateSector = (code: string) => t(`Sectors.${code}`, code);
	const translateDomain = (code: string) => t(`BusinessDomains.${code}`, code);
	const translateRegion = (name: string) => t(`communityDashboard.regionNames.${name}`, name);

	const handleFilterChange = (patch: Partial<CommunityFilterSelection>) => {
		setFilters(current => {
			const next = { ...current, ...patch };
			if (patch.category !== undefined && next.expertise !== ALL_VALUE && dims) {
				const expertise = dims.expertises.find(item => String(item.id) === next.expertise);
				const categoryIndex = dims.expertiseCategories.indexOf(next.category);
				if (next.category !== ALL_VALUE && expertise && expertise.categoryIndex !== categoryIndex) {
					next.expertise = ALL_VALUE;
				}
			}
			return withCountryInRegion(next);
		});
	};

	if (isLoading) {
		return <CommunityDashboardSkeleton />;
	}

	if (isError || !data) {
		return (
			<Box px={4} py={3}>
				<Paper sx={{ p: 4, textAlign: "center", borderRadius: 3 }}>
					<Typography variant="h6" color="error" gutterBottom>
						{t("communityDashboard.errorLoading")}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{t("communityDashboard.tryAgain")}
					</Typography>
				</Paper>
			</Box>
		);
	}

	const { dimensions } = data;
	const n = selection.length;
	const categoryFilterIndex = filterIndexes?.categoryIndex ?? ALL;
	const regionFilterIndex = filterIndexes?.regionIndex ?? ALL;
	const noFilters =
		activeFilters.region === ALL_VALUE &&
		activeFilters.country === ALL_VALUE &&
		activeFilters.category === ALL_VALUE &&
		activeFilters.expertise === ALL_VALUE &&
		activeFilters.sector === ALL_VALUE;

	const regionOptions: SelectOption[] = [
		{ value: ALL_VALUE, label: t("communityDashboard.filters.allRegions") },
		...dimensions.regions.map(region => ({ value: region, label: translateRegion(region) })),
		{ value: NOT_LOCATED_VALUE, label: t("communityDashboard.notSpecified") }
	];

	const countryOptions: SelectOption[] = [
		{ value: ALL_VALUE, label: t("communityDashboard.filters.allCountries") },
		...dimensions.countries
			.filter(
				country =>
					regionFilterIndex === ALL ||
					(countryCodesByRegion.get(regionFilterIndex)?.has(country.code) ?? false)
			)
			.map(country => ({
				value: country.code,
				label: countryLabel(country, regionDisplayNames, country.code)
			}))
			.sort((a, b) => a.label.localeCompare(b.label, locale)),
		{ value: NOT_LOCATED_VALUE, label: t("communityDashboard.notSpecified") }
	];

	const categoryOptions: SelectOption[] = [
		{ value: ALL_VALUE, label: t("communityDashboard.filters.all") },
		...dimensions.expertiseCategories.map(code => ({
			value: code,
			label: translateCategory(code)
		}))
	];

	const expertiseOptions: SelectOption[] = [
		{ value: ALL_VALUE, label: t("communityDashboard.filters.allExpertises") },
		...dimensions.expertises
			.filter(
				expertise =>
					categoryFilterIndex === ALL || expertise.categoryIndex === categoryFilterIndex
			)
			.map(expertise => ({
				value: String(expertise.id),
				label: translateExpertise(expertise.code)
			}))
			.sort((a, b) => a.label.localeCompare(b.label, locale))
	];

	const sectorOptions: SelectOption[] = [
		{ value: ALL_VALUE, label: t("communityDashboard.filters.allSectors") },
		...dimensions.sectors
			.map(code => ({ value: code, label: translateSector(code) }))
			.sort((a, b) => a.label.localeCompare(b.label, locale))
	];

	const categoryCounts = countSingle(selection, talent => talent.categoryIndex);
	const locatedCount = countLocated(selection);

	const kpiItems: KpiItem[] = [
		{
			label: t("communityDashboard.kpi.talents"),
			value: formatCount(n, locale),
			detail: noFilters
				? t("communityDashboard.kpi.fullCommunity")
				: t("communityDashboard.kpi.filteredSelection"),
			color: zoullooColors.secondary.dark
		},
		...dimensions.expertiseCategories.map((code, index) => ({
			label: t("communityDashboard.kpi.profiles", { category: translateCategory(code) }),
			value: formatCount(categoryCounts[index] || 0, locale),
			detail:
				n > 0
					? t("communityDashboard.kpi.ofSelection", {
							pct: formatPct(((categoryCounts[index] || 0) * 100) / n, locale)
						})
					: undefined,
			color: categoryColor(index)
		})),
		{
			label: t("communityDashboard.kpi.located"),
			value: formatCount(locatedCount, locale),
			detail:
				n > 0
					? t("communityDashboard.kpi.ofSelection", {
							pct: formatPct((locatedCount * 100) / n, locale)
						})
					: undefined,
			color: zoullooColors.secondary.dark
		}
	];

	const expertiseRows: ChartRow[] = toSortedRows(
		countSingle(selection, talent => talent.expertiseIndex),
		n
	).map(row => ({
		id: String(dimensions.expertises[row.key]?.id ?? row.key),
		label: translateExpertise(dimensions.expertises[row.key]?.code ?? String(row.key)),
		count: row.count,
		pct: row.pct,
		color: categoryColor(dimensions.expertises[row.key]?.categoryIndex ?? -1)
	}));

	const seniorityCounts = countSingle(selection, talent => talent.seniorityIndex, NOT_SPECIFIED);
	const notSpecifiedSeniorityCount = seniorityCounts[NOT_SPECIFIED] || 0;
	const seniorityRows: ChartRow[] = [
		...dimensions.seniorities.map((code, index) => ({
			id: String(index),
			label: translateSeniority(code),
			count: seniorityCounts[index] || 0,
			pct: n > 0 ? ((seniorityCounts[index] || 0) * 100) / n : 0,
			color: seniorityColors[index] || fallbackColor
		})),
		...(notSpecifiedSeniorityCount > 0
			? [
					{
						id: String(NOT_SPECIFIED),
						label: t("communityDashboard.notSpecified"),
						count: notSpecifiedSeniorityCount,
						pct: (notSpecifiedSeniorityCount * 100) / n,
						color: mutedColor
					}
				]
			: [])
	];

	const sectorRows: ChartRow[] = toSortedRows(
		countMulti(selection, talent => talent.sectorIndexes),
		n
	).map(row => ({
		id: String(row.key),
		label: translateSector(dimensions.sectors[row.key] ?? String(row.key)),
		count: row.count,
		pct: row.pct,
		color: zoullooColors.secondary.main
	}));

	const domainRows: ChartRow[] = toSortedRows(
		countMulti(selection, talent => talent.businessDomainIndexes),
		n
	).map(row => ({
		id: String(row.key),
		label: translateDomain(dimensions.businessDomains[row.key] ?? String(row.key)),
		count: row.count,
		pct: row.pct,
		color: zoullooColors.secondary.main
	}));

	const regionRows: ChartRow[] = toSortedRows(
		countSingle(selection, talent => talent.regionIndex, NOT_LOCATED),
		n
	).map(row => ({
		id: String(row.key),
		label:
			row.key === NOT_LOCATED
				? t("communityDashboard.notSpecified")
				: translateRegion(dimensions.regions[row.key] ?? String(row.key)),
		count: row.count,
		pct: row.pct,
		color: row.key === NOT_LOCATED ? mutedColor : zoullooColors.secondary.main
	}));

	const countryRows: ChartRow[] = toSortedRows(
		countSingle(selection, talent => talent.countryIndex, NOT_LOCATED),
		n
	).map(row => ({
		id: String(row.key),
		label:
			row.key === NOT_LOCATED
				? t("communityDashboard.notSpecified")
				: countryLabel(dimensions.countries[row.key], regionDisplayNames, String(row.key)),
		count: row.count,
		pct: row.pct,
		color: row.key === NOT_LOCATED ? mutedColor : zoullooColors.secondary.main
	}));

	const experienceRegionRows: ChartRow[] = toSortedRows(
		countMulti(selection, talent => talent.experienceRegionIndexes),
		n
	).map(row => ({
		id: String(row.key),
		label: translateRegion(dimensions.experienceRegions[row.key] ?? String(row.key)),
		count: row.count,
		pct: row.pct,
		color: zoullooColors.secondary.main
	}));

	return (
		<Box px={4} py={3}>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
				<Box>
					<Typography variant="h4" fontWeight={700} color="text.primary">
						{t("communityDashboard.title")}
					</Typography>
					<Typography variant="body2" color="text.secondary" mt={0.5}>
						{t("communityDashboard.subtitle")}
					</Typography>
				</Box>
				<IconButton
					onClick={() => refetch()}
					aria-label={t("communityDashboard.refresh")}
					sx={{
						p: 1,
						"&:hover": { backgroundColor: zoullooColors.background.light }
					}}
				>
					{isFetching ? (
						<CircularProgress size={20} />
					) : (
						<RefreshIcon sx={{ color: "text.secondary" }} />
					)}
				</IconButton>
			</Box>

			<Box mb={3}>
				<CommunityFilterBar
					regionOptions={regionOptions}
					countryOptions={countryOptions}
					categoryOptions={categoryOptions}
					expertiseOptions={expertiseOptions}
					sectorOptions={sectorOptions}
					filters={activeFilters}
					onChange={handleFilterChange}
					unit={unit}
					onUnitChange={setUnit}
					selectionLabel={t("communityDashboard.selectionCount", { count: n })}
				/>
			</Box>

			<Box mb={4}>
				<KpiRow items={kpiItems} />
			</Box>

			<Grid container spacing={3}>
				<Grid size={{ xs: 12, md: 7 }}>
					<HBarCard
						title={t("communityDashboard.cards.structure.title")}
						note={t("communityDashboard.cards.structure.note")}
						rows={expertiseRows}
						unit={unit}
						dimensionLabel={t("communityDashboard.cards.structure.dimension")}
						labelWidth={210}
						legend={dimensions.expertiseCategories.map((code, index) => ({
							label: translateCategory(code),
							color: categoryColor(index)
						}))}
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 5 }}>
					<DonutCard
						title={t("communityDashboard.cards.seniority.title")}
						note={t("communityDashboard.cards.seniority.note")}
						rows={seniorityRows}
						dimensionLabel={t("communityDashboard.cards.seniority.dimension")}
						centerTop={formatCount(n, locale)}
						centerBottom={t("communityDashboard.talentsUnit", { count: n })}
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 6 }}>
					<HBarCard
						title={t("communityDashboard.cards.sectors.title")}
						note={t("communityDashboard.cards.sectors.note")}
						rows={sectorRows}
						unit={unit}
						dimensionLabel={t("communityDashboard.cards.sectors.dimension")}
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 6 }}>
					<HBarCard
						title={t("communityDashboard.cards.domains.title")}
						note={t("communityDashboard.cards.domains.note")}
						rows={domainRows}
						unit={unit}
						dimensionLabel={t("communityDashboard.cards.domains.dimension")}
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 6 }}>
					<HBarCard
						title={t("communityDashboard.cards.regions.title")}
						note={t("communityDashboard.cards.regions.note")}
						rows={regionRows}
						unit={unit}
						dimensionLabel={t("communityDashboard.cards.regions.dimension")}
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 6 }}>
					<HBarCard
						title={t("communityDashboard.cards.experienceRegions.title")}
						note={t("communityDashboard.cards.experienceRegions.note")}
						rows={experienceRegionRows}
						unit={unit}
						dimensionLabel={t("communityDashboard.cards.experienceRegions.dimension")}
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 6 }}>
					<HBarCard
						title={t("communityDashboard.cards.countries.title")}
						note={t("communityDashboard.cards.countries.note")}
						rows={countryRows}
						unit={unit}
						dimensionLabel={t("communityDashboard.cards.countries.dimension")}
					/>
				</Grid>
			</Grid>
		</Box>
	);
};

export default CommunityDashboard;
